// Third-party imports
import React from 'react';
import { AccessDenied, Admin, Authenticated, CustomRoutes, Resource, CanAccess } from 'react-admin';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

// Local imports - providers
import authProvider from './providers/authProvider';
import dataProvider from './providers/dataProvider';

// Local imports - components
import ApiDocumentation from './components/ApiDocumentation';
import {
  OktaLogin,
  OktaCallback,
  BadgeLogin,
  MagicLinkConfirm,
  MagicLinkCallback,
} from './components/Auth';
import { ConfigVersionCreate, ConfigVersionList } from './components/configVersions';
import CustomLayout from './components/CustomLayout';
import CustomLogin from './components/CustomLogin';
import { DashboardResources, pluginResourceProps } from '@src/types';
import PartnerSelect from './components/PartnerSelect';
import { PluginEdit, SettingsTabShell, SETTINGS_TABS } from './components/plugins';
import { SectionIndexRedirect } from './components/SectionTabShell';
import { Profile } from './profiles';
import { slateInkTheme } from './theme/slateInkTheme';
import WelcomeDashboard from './components/WelcomeDashboard';
import { CatalogItemList, CatalogItemEdit, CatalogFieldsMappingUpload, CatalogRulesPage, VariantSkuEdit, CatalogUploaderPage, UploadProgressPage, CatalogCodeMappingsPage, AddColorCodeMapping, AddSizeCodeMapping, EditColorCodeMapping, EditSizeCodeMapping, CatalogTabShell, CATALOG_TABS } from './components/catalog';
import { RepairsList, RepairEdit, RepairsTabShell, REPAIRS_TABS } from './components/repairs';
import { ThemeVersionsPage, isStorefrontThemeEnabled } from './components/themeVersions';
import { LookerDashboardsPage } from './lookerDashboards';
import { FilesRouteGuard } from './files';
import {
  pricingRulesResource,
  conditionDiscountsResource,
  itemAdjustedPricesResource,
  PricingRulesHistory,
  ConditionDiscountsHistory,
  ResalePricingOverview,
  RoundingRulesPage,
  StagingReviewPage,
} from './resalePricing';
import repairPricingResource from './repairPricing';
import RepairPricingList from './repairPricing/RepairPricingList';
import shopifyRedirectsResource from './shopifyRedirects';
import ShopifyRedirectsList from './shopifyRedirects/ShopifyRedirectsList';
import shopifySync from './shopifySync';
import QueueStatusList from './shopifySync/QueueStatusList';
import { StorefrontTabShell, STOREFRONT_TABS } from './storefront';
import { DeveloperTabShell, DEVELOPER_TABS, PLUGIN_GUIDES, PluginGuidePage } from './developer';
import inventoryItemsResource from './inventoryItems';
import giftcardsResource from './giftCards';
import ordersResource from './orders';
import tradeinsResource from './tradeins';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Okta SSO — /sso avoids conflicting with react-admin's /login redirect */}
        <Route path="/sso" element={<OktaLogin />} />
        <Route path="/authorization-code/callback" element={<OktaCallback />} />

        <Route path="/badge-login" element={<BadgeLogin />} />

        <Route path="/magic-link-confirm" element={<MagicLinkConfirm />} />
        <Route path="/magic-link" element={<MagicLinkCallback />} />

        <Route
          path="*"
          element={
            <Admin
              authProvider={authProvider}
              dataProvider={dataProvider}
              layout={CustomLayout}
              theme={slateInkTheme}
              darkTheme={null}
              title="Trove Partner Dashboard"
              loginPage={CustomLogin}
              dashboard={WelcomeDashboard}
              requireAuth
            >
              {/* Define catalog resources for react-admin data layer */}
              <Resource
                name={DashboardResources.CatalogItems}
                list={<CatalogTabShell tab="products"><CatalogItemList /></CatalogTabShell>}
                edit={CatalogItemEdit}
                recordRepresentation="title"
                options={{ label: 'Catalog Item' }}
              />
              <Resource
                name={DashboardResources.VariantSkus}
                edit={VariantSkuEdit}
                recordRepresentation="sku"
                options={{ label: 'Variant SKU' }}
              />
              {pluginResourceProps.map(({ name, options }) =>
                <Resource
                  key={name}
                  name={name}
                  create={<ConfigVersionCreate label={options?.label || ''} />}
                  edit={<PluginEdit label={options?.label || ''} />}
                  list={
                    <SettingsTabShell tab={name}>
                      <ConfigVersionList label={options?.label || ''} />
                    </SettingsTabShell>
                  }
                  options={options}
                />)}
              <Resource
                name={DashboardResources.Repairs}
                list={<RepairsTabShell tab="submissions"><RepairsList /></RepairsTabShell>}
                edit={RepairEdit}
                recordRepresentation="uuid"
                options={{ label: 'Repairs' }}
              />
              <Resource {...pricingRulesResource}>
                <Route path="history" element={<PricingRulesHistory />} />
              </Resource>
              <Resource {...conditionDiscountsResource}>
                <Route path="history" element={<ConditionDiscountsHistory />} />
              </Resource>
              <Resource {...itemAdjustedPricesResource} />
              <Resource
                {...repairPricingResource}
                list={<RepairsTabShell tab="pricing"><RepairPricingList /></RepairsTabShell>}
              />
              <Resource
                {...shopifyRedirectsResource}
                list={<StorefrontTabShell tab="redirects"><ShopifyRedirectsList /></StorefrontTabShell>}
              />
              <Resource
                {...shopifySync}
                list={<StorefrontTabShell tab="sync"><QueueStatusList /></StorefrontTabShell>}
              />
              <Resource {...inventoryItemsResource} />
              <Resource {...giftcardsResource} />
              <Resource {...ordersResource} />
              <Resource {...tradeinsResource} />

              <CustomRoutes>
                {/* Partner Selection - shows after login before main app */}
                <Route path="/partners" element={<PartnerSelect />} />
                {/* Resale Pricing tabs without a backing resource. Overview lives at the
                    section root (/resale); the resource tabs (pricingRules, …)
                    register their own routes via <Resource> above. */}
                <Route
                  path="/resale"
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.PricingRules} action="list" accessDenied={<AccessDenied />}>
                        <ResalePricingOverview />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Alias — the Overview tab lives at the section root */}
                <Route path="/resale/overview" element={<Navigate to="/resale" replace />} />
                <Route
                  path="/resale/rounding"
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.PricingRules} action="list" accessDenied={<AccessDenied />}>
                        <RoundingRulesPage />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                <Route
                  path="/resale/overrides/:adjustmentId/review"
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.PricingRules} action="list" accessDenied={<AccessDenied />}>
                        <StagingReviewPage />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Section roots open the first tab the user can actually see */}
                <Route
                  path="/catalog"
                  element={
                    <Authenticated>
                      <SectionIndexRedirect tabs={CATALOG_TABS} />
                    </Authenticated>
                  }
                />
                <Route
                  path="/repairs"
                  element={
                    <Authenticated>
                      <SectionIndexRedirect tabs={REPAIRS_TABS} />
                    </Authenticated>
                  }
                />
                <Route
                  path="/storefront"
                  element={
                    <Authenticated>
                      <SectionIndexRedirect tabs={STOREFRONT_TABS} />
                    </Authenticated>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <Authenticated>
                      <SectionIndexRedirect tabs={SETTINGS_TABS} />
                    </Authenticated>
                  }
                />
                {/* Catalog Rules - if/then rules for catalog records */}
                <Route
                  path={`/${DashboardResources.CatalogRules}`}
                  element={
                    <Authenticated>
                      <CatalogTabShell tab="rules">
                        <CatalogRulesPage />
                      </CatalogTabShell>
                    </Authenticated>
                  }
                />
                {/* Catalog Uploader - CSV/TSV catalog file upload with scheduling.
                    These leaf pages sit outside CatalogTabShell, so they carry their
                    own CanAccess rather than inheriting the section's tab check. */}
                <Route
                  path={`/${DashboardResources.CatalogUploader}/:uploadId`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogUploader} action="list" accessDenied={<AccessDenied />}>
                        <UploadProgressPage />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                <Route
                  path={`/${DashboardResources.CatalogUploader}`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogUploader} action="list" accessDenied={<AccessDenied />}>
                        <CatalogUploaderPage />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Catalog Fields Mapping Upload - bulk upload display type mappings */}
                <Route
                  path={`/${DashboardResources.CatalogFieldsMappingUpload}`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogFieldsMappingUpload} action="list" accessDenied={<AccessDenied />}>
                        <CatalogFieldsMappingUpload />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Catalog Code Mappings - size and color property management */}
                <Route
                  path={`/${DashboardResources.CatalogCodeMappings}/colors/:id`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogCodeMappings} action="list" accessDenied={<AccessDenied />}>
                        <EditColorCodeMapping />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                <Route
                  path={`/${DashboardResources.CatalogCodeMappings}/sizes/:id`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogCodeMappings} action="list" accessDenied={<AccessDenied />}>
                        <EditSizeCodeMapping />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                <Route
                  path={`/${DashboardResources.CatalogCodeMappings}/colors/add`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogCodeMappings} action="list" accessDenied={<AccessDenied />}>
                        <AddColorCodeMapping />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                <Route
                  path={`/${DashboardResources.CatalogCodeMappings}/sizes/add`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.CatalogCodeMappings} action="list" accessDenied={<AccessDenied />}>
                        <AddSizeCodeMapping />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                <Route
                  path={`/${DashboardResources.CatalogCodeMappings}`}
                  element={
                    <Authenticated>
                      <CatalogTabShell tab="attributes">
                        <CatalogCodeMappingsPage />
                      </CatalogTabShell>
                    </Authenticated>
                  }
                />
                {/* User Profile */}
                <Route
                  path="/profile"
                  element={
                    <Authenticated>
                      <Profile />
                    </Authenticated>
                  }
                />
                {/* Developer: API docs + plugin installation guides */}
                <Route
                  path="/dev"
                  element={
                    <Authenticated>
                      <SectionIndexRedirect tabs={DEVELOPER_TABS} />
                    </Authenticated>
                  }
                />
                <Route
                  path={`/${DashboardResources.ApiDocs}`}
                  element={
                    <Authenticated>
                      <DeveloperTabShell tab={DashboardResources.ApiDocs}>
                        <ApiDocumentation />
                      </DeveloperTabShell>
                    </Authenticated>
                  }
                />
                {PLUGIN_GUIDES.map(({ resource, guide }) => (
                  <Route
                    key={resource}
                    path={`/${resource}`}
                    element={
                      <Authenticated>
                        <DeveloperTabShell tab={resource}>
                          <PluginGuidePage guide={guide} />
                        </DeveloperTabShell>
                      </Authenticated>
                    }
                  />
                ))}
                {/* Storefront theme versions — feature-flag + permission gated (dark until both pass) */}
                {isStorefrontThemeEnabled() && (
                  <Route
                    path={`/${DashboardResources.ThemeVersions}`}
                    element={
                      <Authenticated>
                        <CanAccess resource={DashboardResources.ThemeVersions} action="show" accessDenied={<AccessDenied />}>
                          <StorefrontTabShell tab="theme">
                            <ThemeVersionsPage />
                          </StorefrontTabShell>
                        </CanAccess>
                      </Authenticated>
                    }
                  />
                )}
                {/* Embedded Looker dashboards - only relevant for partners with an active Looker service */}
                <Route
                  path={`/${DashboardResources.LookerDashboards}`}
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.LookerDashboards} action="show" accessDenied={<AccessDenied />}>
                        <LookerDashboardsPage />
                      </CanAccess>
                    </Authenticated>
                  }
                />
                {/* Data Feeds > Files - SFTP-backed S3 file browser, scoped to the partner's allowed folders.
                    FilesRouteGuard additionally requires SFTP folder mapping to be configured for the
                    partner, mirroring the check CustomMenu uses to hide the nav item — otherwise a user
                    could reach the page by navigating to /files directly even when the nav hides it. */}
                <Route
                  path="/files"
                  element={
                    <Authenticated>
                      <CanAccess resource={DashboardResources.Files} action="list" accessDenied={<AccessDenied />}>
                        <FilesRouteGuard />
                      </CanAccess>
                    </Authenticated>
                  }
                />
              </CustomRoutes>
            </Admin>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
